import React, {
  useState, useEffect, useMemo, useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  withStyles, Dialog, Tabs, Tab,
} from '@material-ui/core';
import {
  Create,
  Close,
} from '@material-ui/icons';
import { getOperators, getOperatorsGroups } from '~/store/manager/actions';
import {
  getCodeGroups as getMaintenanceCodeGroups,
  getCodes as getMaintenanceCodes,
  getCodeTypes as getMaintenanceCodeTypes,
} from '~/store/maintenance/actions';
import {
  getEquipmentScheduleStatuses,
  getPreventiveTypes,
  setCurrentEquipmentStatus,
  setOriginalCurrentEquipmentStatus,
} from '~/store/dispatch/actions';
import {
  Card, CardBody, CardHeader, CardIcon,
} from '~/components/Card';
import { usePlatformConfigurationAuth } from '~/services/platformConfiguration';
import { toCodesMapByKey } from '~/utils/codes';
import { TAB, TAB_CONFIG } from './constants';
import { Header } from './Header';
import {
  toMapById,
  createInitialModalState,
  resetModalState,
} from './utils';
import StatusTab from './StatusTab';
import ScheduleTab from './ScheduleTab';
import ChatTab from './ChatTab';
import styles from './styles';

const EquipmentStatusModal = ({
  open, setOpen, equip, classes,
}) => {
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  const operators = useSelector(state => state.manager.operators);
  const codes = useSelector(state => state.nref.codes);
  const elements = useSelector(state => state.nref.elements);
  const subElements = useSelector(state => state.nref.subElements);
  const maintenanceCodes = useSelector(state => state.maintenance.codes);
  const config = useSelector(state => state.auth.user.config);
  const closures = useSelector(state => state.manager.closures);
  const userAccessLevel = useSelector(state => state.auth.user.level);
  const chatStatus = useSelector(
    state => state.manager.platformConfiguration.find(
      e => e.key === 'enable_chat',
    ),
  );
  // eslint-disable-next-line no-underscore-dangle
  const userRoles = useSelector(state => state.auth.user?._roles);

  const dispatchStatusCheckbox = usePlatformConfigurationAuth('dispatch_equipmentstatus_checkbox') || '0';
  const checkboxValue = Number(dispatchStatusCheckbox?.value ?? 0);

  const [tab, setTab] = useState(TAB.STATUS);
  const [currentModalStates, setCurrentModalStates] = useState(
    () => createInitialModalState(equip, checkboxValue),
  );

  const enabledChatStatus = chatStatus?.value === '1';

  const hasDispatchPermission = roles => Array.isArray(roles)
  && roles.some(role => role?.module_value === 'DESPACHO');

  const dispatchPermission = hasDispatchPermission(userRoles);

  useEffect(() => {
    dispatch(getOperators());
    dispatch(getOperatorsGroups());
    dispatch(getMaintenanceCodes());
    dispatch(getMaintenanceCodeTypes());
    dispatch(getMaintenanceCodeGroups());
  }, [dispatch, open, equip?.id_equip]);

  useEffect(() => {
    dispatch(getEquipmentScheduleStatuses());
    dispatch(getPreventiveTypes());
  }, [dispatch]);

  useEffect(() => {
    if (!open) return;
    setCurrentModalStates(createInitialModalState(equip, checkboxValue));
    setTab(TAB.STATUS);
  }, [open]);

  const lookupCtx = useMemo(() => ({
    config,
    closures,
    userAccessLevel,
    operatorsById: toMapById(operators, 'id_operator'),
    codes,
    codesByKey: toCodesMapByKey(codes),
    elementsById: toMapById(elements, 'id'),
    subElementsById: toMapById(subElements, 'id'),
    maintenanceCodesById: toMapById(maintenanceCodes, 'id'),
  }), [
    operators, codes, config, closures, userAccessLevel,
    elements, subElements, maintenanceCodes,
  ]);

  const setStatusState = useCallback((statusUpdates) => {
    setCurrentModalStates(prev => ({
      ...prev,
      status: {
        ...prev.status,
        ...statusUpdates,
      },
    }));
  }, []);

  const setScheduleState = useCallback((scheduleUpdates) => {
    setCurrentModalStates(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        ...scheduleUpdates,
      },
    }));
  }, []);

  const handleClose = useCallback(() => {
    setCurrentModalStates(resetModalState());
    dispatch(setOriginalCurrentEquipmentStatus(null));
    dispatch(setCurrentEquipmentStatus(null));
    setOpen(false);
  }, [dispatch, setOpen]);

  const handleTabChange = (_, newValue) => {
    setTab(newValue);
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        className: classes.dialogPaper,
      }}
      classes={{
        root: classes.dialogRoot,
      }}
      onClose={handleClose}
      container={document.fullscreenElement || document.body}
      scroll="body"
    >
      <div className={classes.wrapperModal}>
        <CardHeader icon className={classes.wrapperCardHeader}>
          <CardIcon color="warning" className={classes.cardIconWarning}>
            <Create className={classes.headerIcons} />
          </CardIcon>
          <CardIcon className={classes.cardIconClose} onClick={() => handleClose()}>
            <Close className={classes.headerIcons} />
          </CardIcon>
        </CardHeader>
        <div className={classes.wrapperContent}>
          <Card className={classes.cardMain}>
            <Header
              classes={classes}
              equipment={equip}
              translate={translate}
            />
            <CardBody className={classes.cardBody}>
              <Tabs
                value={tab}
                onChange={handleTabChange}
                classes={{
                  flexContainer: classes.tabsFlexContainer,
                }}
                TabIndicatorProps={{ className: classes.tabIndicator }}
              >
                {TAB_CONFIG.map(({ key, label, disabled }) => (
                  <Tab
                    key={key}
                    value={key}
                    label={translate(label)}
                    disabled={disabled({ dispatchPermission, enabledChatStatus })}
                    classes={{
                      root: classes.tab,
                      selected: classes.tabSelected,
                      disabled: classes.tabDisabled,
                    }}
                  />
                ))}
              </Tabs>
              {tab === TAB.STATUS && (
                <StatusTab
                  classes={classes}
                  currentModalStates={currentModalStates.status}
                  setStatusState={setStatusState}
                  lookupCtx={lookupCtx}
                  onClose={handleClose}
                />
              )}
              {tab === TAB.SCHEDULE && (
                <ScheduleTab
                  classes={classes}
                  currentModalStates={currentModalStates.schedule}
                  setScheduleState={setScheduleState}
                  lookupCtx={lookupCtx}
                  onClose={handleClose}
                />
              )}
              {tab === TAB.CHAT && (
                <ChatTab
                  classes={classes}
                  equipment={equip}
                />
              )}
            </CardBody>
          </Card>
        </div>
      </div>
    </Dialog>
  );
};

export default withStyles(styles)(EquipmentStatusModal);

EquipmentStatusModal.propTypes = {
  classes: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  equip: PropTypes.object.isRequired,
};
